import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone,
  Mail,
  MapPin,
  ArrowRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import FormSuccess from '../components/FormSuccess';

const Contact = () => {
  const { t } = useTranslation();
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': form.getAttribute('name') || '',
          ...formData
        }).toString()
      });
      
      if (response.ok) {
        setShowSuccess(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Villa kom upp við að senda form. Vinsamlegast reyndu aftur.');
    }
  };

  const contactInfo = {
    phone: "(+354) 537-7990",
    email: "hallo@vefsida.is",
    locations: [
      "Reykjavík, Ísland",
      "Kaupmannahöfn, Danmörk",
      "Barcelona, Spánn"
    ]
  };

  return (
    <>
      <SEO 
        title={t('common.contact')}
        description="Hafðu samband við okkur og við finnum bestu lausnina fyrir þitt fyrirtæki. Fagleg ráðgjöf og framúrskarandi þjónusta."
        keywords="hafa samband, ráðgjöf, þjónusta, fyrirspurn"
      />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Hafðu samband
            </h1>
            <p className="text-xl text-gray-600">
              Við erum ávallt tilbúin að aðstoða þig og svara öllum þínum spurningum
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white rounded-2xl shadow-sm p-8">
                <form 
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <input type="hidden" name="form-name" value="contact" />
                  <p hidden>
                    <label>
                      Don't fill this out: <input name="bot-field" />
                    </label>
                  </p>
                  
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Upplýsingar</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                          Nafn
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Netfang
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                          Sími
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                          Fyrirtæki
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Efni
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Skilaboð
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={6}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <motion.button
                    type="submit"
                    className="w-full bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center justify-center"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Senda skilaboð
                    <ArrowRight className="ml-2" />
                  </motion.button>
                </form>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              className="lg:col-span-1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="bg-white rounded-2xl shadow-sm p-8 sticky top-32">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Hafðu samband</h3>
                <div className="space-y-6">
                  <motion.div 
                    className="flex items-start"
                    whileHover={{ x: 5 }}
                  >
                    <Phone className="w-6 h-6 text-[#EA295A] mr-4" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Sími</h4>
                      <p className="mt-1 text-sm text-gray-600">{contactInfo.phone}</p>
                    </div>
                  </motion.div>
                  <motion.div 
                    className="flex items-start"
                    whileHover={{ x: 5 }}
                  >
                    <Mail className="w-6 h-6 text-[#EA295A] mr-4" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Netfang</h4>
                      <p className="mt-1 text-sm text-gray-600">{contactInfo.email}</p>
                    </div>
                  </motion.div>
                  <motion.div 
                    className="flex items-start"
                    whileHover={{ x: 5 }}
                  >
                    <MapPin className="w-6 h-6 text-[#EA295A] mr-4" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Staðsetningar</h4>
                      {contactInfo.locations.map((location, index) => (
                        <p key={index} className="mt-1 text-sm text-gray-600">{location}</p>
                      ))}
                    </div>
                  </motion.div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-100">
                  <h4 className="text-sm font-medium text-gray-900 mb-4">Opnunartími</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Mánudagur - Föstudagur</span>
                      <span className="text-gray-900">9:00 - 17:00</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Laugardagur</span>
                      <span className="text-gray-900">Lokað</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Sunnudagur</span>
                      <span className="text-gray-900">Lokað</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {showSuccess && (
        <FormSuccess 
          message="Takk fyrir skilaboðin! Við munum hafa samband við þig eins fljótt og auðið er."
          onClose={() => setShowSuccess(false)}
        />
      )}
    </>
  );
};

export default Contact;